/**
 * Der Ort fuer die Wettervorhersage.
 *
 * Liegt in `meta`, nicht im UI-Store: es ist eine echte Einstellung, die eine
 * Sicherung ueberdauern soll.
 */
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { setMeta } from '@/db/repositories/meta';
import { DEFAULT_LOCATION, type Coordinates } from '@/lib/weather/model';

export const META_LOCATION = 'weatherLocation';
export const META_WEATHER_ENABLED = 'weatherEnabled';

export function useWeatherSettings(): { location: Coordinates; enabled: boolean } {
  return useLiveQuery(
    async () => ({
      location: ((await db.meta.get(META_LOCATION))?.value as Coordinates) ?? DEFAULT_LOCATION,
      // Standardmaessig aus: kein Abruf nach aussen ohne Zutun des Nutzers.
      enabled: ((await db.meta.get(META_WEATHER_ENABLED))?.value as boolean) ?? false,
    }),
    [],
    { location: DEFAULT_LOCATION, enabled: false },
  );
}

export async function setWeatherEnabled(enabled: boolean): Promise<void> {
  await setMeta(META_WEATHER_ENABLED, enabled);
}

export async function setWeatherLocation(location: Coordinates): Promise<void> {
  await setMeta(META_LOCATION, location);
}

/**
 * Standort ueber den Browser bestimmen. Fragt den Nutzer um Erlaubnis; ein
 * "Nein" ist kein Fehler, sondern liefert einfach `null`.
 */
export async function detectLocation(): Promise<Coordinates | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: Math.round(pos.coords.latitude * 100) / 100,
        longitude: Math.round(pos.coords.longitude * 100) / 100,
        name: 'Mein Standort',
      }),
      () => resolve(null),
      { timeout: 10_000, maximumAge: 600_000 },
    );
  });
}
